'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["collapsable", "filterKey", "getFilterValue", "multi", "options", "SelectComponent", "sortFn", "tooltipMessage", "value"];
import { useCallback, useMemo, memo, useContext } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import FormattedMessage from 'I18n/components/FormattedMessage';
import localeComparator from 'I18n/utils/localeComparator';
import unescapedText from 'I18n/utils/unescapedText';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UISelect from 'UIComponents/input/UISelect';
import { getIsPrerenderUserAgent } from 'marketplace-ui-common/utils/userAgent';
import { updateStorefrontFilters } from '../../slices/storefrontFiltersSlice';
import StorefrontUsageTrackerContext from '../../context/StorefrontUsageTrackerContext';
import BasePrerenderFilter from './BasePrerenderFilter';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import { SERVICE } from '../../constants/search/FilterKey';
import { getCatalogServiceTrackingName } from '../../utils/i18n';
import { getServices } from '../../slices/serviceCatalogSlice';
import { jsx as _jsx } from "react/jsx-runtime";
const textKey = 'ecosystem-marketplace-solutions-ui.storefront.filters';
const defaultGetFilterValue = evt => evt.target.value;
const alphabeticalSortOptions = (optA, optB) => localeComparator(optA.text, optB.text);
const ValueComponent = ({
  children
}) => /*#__PURE__*/_jsx("span", {
  role: "button",
  children: children
});
const BaseFilter = props => {
  const {
      collapsable,
      filterKey,
      getFilterValue,
      multi,
      options,
      SelectComponent,
      sortFn,
      tooltipMessage,
      value
    } = props,
    passThroughProps = _objectWithoutPropertiesLoose(props, _excluded);
  const isPrerender = getIsPrerenderUserAgent();
  const dispatch = useDispatch();
  const {
    trackInteraction
  } = useContext(StorefrontUsageTrackerContext);
  const catalogServices = useSelector(getServices);
  const onFilterChange = useCallback(evt => {
    const filterValue = getFilterValue(evt);
    dispatch(updateStorefrontFilters({
      updates: {
        [filterKey]: filterValue
      }
    }));
    trackInteraction('change filter', {
      filter: filterKey,
      value: filterKey === SERVICE ? filterValue.map(serviceId => getCatalogServiceTrackingName(serviceId, catalogServices)) : filterValue
    });
  }, [catalogServices, dispatch, filterKey, getFilterValue, trackInteraction]);
  const sortedOptions = useMemo(() => options && options.sort(sortFn), [options, sortFn]);
  const baseProps = useMemo(() => ({
    anchorType: 'button',
    clearable: !multi,
    multi,
    options: sortedOptions,
    placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
      message: `${textKey}.${filterKey}.placeholder`
    }),
    value,
    valueComponent: ValueComponent
  }), [filterKey, multi, sortedOptions, value]);
  return /*#__PURE__*/_jsx(UIFormControl, {
    label: !collapsable && (tooltipMessage ? /*#__PURE__*/_jsx(UITooltip, {
      title: tooltipMessage,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `${textKey}.${filterKey}.label`
      })
    }) : /*#__PURE__*/_jsx(FormattedMessage, {
      message: `${textKey}.${filterKey}.label`
    })),
    labelClassName: "is--heading-7 m-bottom-0",
    "aria-label": collapsable && unescapedText(`${textKey}.${filterKey}.label`),
    children: isPrerender ? /*#__PURE__*/_jsx(BasePrerenderFilter, Object.assign({
      SelectComponent: SelectComponent,
      filterKey: filterKey
    }, baseProps, passThroughProps)) : /*#__PURE__*/_jsx(SelectComponent, Object.assign({
      onChange: onFilterChange
    }, baseProps, passThroughProps))
  });
};
BaseFilter.propTypes = {
  filterKey: PropTypes.string.isRequired,
  getFilterValue: PropTypes.func,
  collapsable: PropTypes.bool,
  multi: PropTypes.bool,
  options: PropTypes.array,
  SelectComponent: PropTypes.elementType,
  sortFn: PropTypes.func,
  tooltipMessage: PropTypes.object,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.array])
};
BaseFilter.defaultProps = {
  getFilterValue: defaultGetFilterValue,
  sortFn: alphabeticalSortOptions,
  SelectComponent: UISelect
};
export default /*#__PURE__*/memo(BaseFilter);